import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ShoppingCart, X, Trash2, Minus, Plus } from 'lucide-react';
import { billingApi, type CartData } from '../api/billing';

/**
 * Cart store + pop-up widget.
 *
 * Owner directive 2026-09-26: the cart lives on checkout screens; a pop-up store
 * widget appears ONLY when there is something actively in the cart. It is a store
 * widget (bottom-right bubble) that expands to review the cart.
 */

interface CartContextValue {
  cart: CartData | null;
  loading: boolean;
  refresh: () => Promise<void>;
  add: (data: { addon_key: string; quantity?: number; domain_name?: string | null; plan_key?: string | null }) => Promise<void>;
  update: (id: string, data: { quantity?: number; domain_name?: string | null }) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clear: () => Promise<void>;
  merge: (token: string) => Promise<void>;
  open: boolean;
  setOpen: (v: boolean) => void;
  checkout: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try { setCart(await billingApi.getCart()); }
    catch { setCart(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Merge a guest cart into the user's cart once signed in (token present).
  useEffect(() => {
    const tok = localStorage.getItem('saik_token');
    const guest = document.cookie.match(/saik_cart_token=([^;]+)/)?.[1];
    if (tok && guest) {
      billingApi.mergeCart(guest).then(setCart).catch(() => {});
    }
  }, []);

  const add = useCallback(async (data: Parameters<CartContextValue['add']>[0]) => {
    const c = await billingApi.addToCart(data);
    setCart(c);
    setOpen(true);
  }, []);
  const update = useCallback(async (id: string, data: { quantity?: number; domain_name?: string | null }) => {
    setCart(await billingApi.updateCartItem(id, data));
  }, []);
  const remove = useCallback(async (id: string) => {
    setCart(await billingApi.removeCartItem(id));
  }, []);
  const clear = useCallback(async () => { setCart(await billingApi.clearCart()); }, []);
  const merge = useCallback(async (token: string) => { setCart(await billingApi.mergeCart(token)); }, []);
  const checkout = useCallback(async () => {
    const res: any = await billingApi.cartCheckout();
    if (res?.stripe_url) { window.location.href = res.stripe_url; return; }
    if (res?.rc_url) { window.location.href = res.rc_url; return; }
    // No payable side (e.g. RC gateway not configured yet) → surface honestly.
    throw new Error(res?.rc_next_step || 'Checkout could not start.');
  }, []);

  const value = useMemo(() => ({
    cart, loading, refresh, add, update, remove, clear, merge, checkout, open, setOpen,
  }), [cart, loading, refresh, add, update, remove, clear, merge, checkout, open]);

  return (
    <CartContext.Provider value={value}>
      {children}
      <CartWidget />
    </CartContext.Provider>
  );
}

/** The pop-up store widget — renders NOTHING when the cart is empty. */
function CartWidget() {
  const { cart, open, setOpen, update, remove, clear, checkout } = useCart();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!cart || cart.line_count === 0) return null;

  const doCheckout = async () => {
    setBusy(true); setErr('');
    try { await checkout(); }
    catch (e: any) { setErr(e?.message || 'Checkout failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50" data-agent-id="cart.widget">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="relative flex items-center gap-2 rounded-full bg-gradient-to-br from-brand-500 to-blue-600 px-4 py-3 text-white shadow-lg transition hover:scale-105"
          aria-label={`Open cart (${cart.item_count} items)`}
        >
          <ShoppingCart size={20} />
          <span className="text-sm font-semibold">Cart</span>
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold text-brand-600 shadow">
            {cart.item_count}
          </span>
        </button>
      ) : (
        <div className="w-[min(92vw,360px)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <ShoppingCart size={16} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-950 dark:text-slate-100">
                Your cart ({cart.item_count})
              </span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close cart"
              className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16} /></button>
          </div>

          <div className="max-h-[50vh] overflow-auto px-4 py-2">
            {cart.items.map(it => (
              <div key={it.id} className="flex items-start justify-between gap-2 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{it.name}</div>
                  {it.domain_name && <div className="truncate text-xs text-slate-400">{it.domain_name}</div>}
                  <div className="mt-1 flex items-center gap-1.5">
                    <button onClick={() => update(it.id, { quantity: it.quantity - 1 })} aria-label={`Decrease ${it.name}`}
                      className="rounded border border-slate-300 p-0.5 text-slate-600 dark:border-slate-600 dark:text-slate-300"><Minus size={12} /></button>
                    <span className="w-6 text-center text-xs tabular-nums">{it.quantity}</span>
                    <button onClick={() => update(it.id, { quantity: it.quantity + 1 })} aria-label={`Increase ${it.name}`}
                      className="rounded border border-slate-300 p-0.5 text-slate-600 dark:border-slate-600 dark:text-slate-300"><Plus size={12} /></button>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{money(it.line_total_cents)}</div>
                  <button onClick={() => remove(it.id)} aria-label={`Remove ${it.name}`}
                    className="mt-1 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-800">
            {err && <p className="mb-2 text-xs text-red-600">{err}</p>}
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Subtotal</span>
              <span className="text-base font-bold text-slate-950 dark:text-slate-100">{money(cart.subtotal_cents)}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={doCheckout} disabled={busy}
                className="flex-1 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
                {busy ? 'Starting…' : 'Checkout'}
              </button>
              <button onClick={() => clear()} aria-label="Clear cart"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-500 hover:text-red-500 dark:border-slate-600">
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
